import React from 'react';
import { Box } from '@mui/material';

export interface SidePanelDockProps {
  /** The page itself. Scrolls in its own column so the panel stays full height. */
  children: React.ReactNode;
  /** What sits in the panel. Rendered only while `open`. */
  panel: React.ReactNode;
  open: boolean;
  /** Remembers the dragged width per page, e.g. "klaris_board_chat_width". */
  storageKey: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  /** Names the drag handle for anyone navigating by keyboard or screen reader. */
  label?: string;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function readStored(key: string, min: number, max: number, fallback: number): number {
  try {
    const stored = Number(window.localStorage.getItem(key));
    if (Number.isFinite(stored) && stored >= min && stored <= max) return stored;
  } catch {
    // Not remembered; the panel still opens at its default.
  }
  return fallback;
}

/**
 * A panel docked down the right of a page, with the page beside it.
 *
 * Beside, not over: the point of docking a chat next to a dashboard is to read
 * the numbers and the answer at the same time, which a drawer that covers them
 * defeats. The page scrolls inside its own column so the panel stays anchored
 * to the full height of the viewport.
 *
 * The width is dragged and remembered per page. A page read beside an answer
 * often wants more room than the default, and re-dragging it on every visit is
 * a papercut; double-clicking the handle puts it back.
 *
 * Give each page its own `storageKey` — two pages sharing one key means
 * widening one narrows the other.
 */
export function SidePanelDock({
  children,
  panel,
  open,
  storageKey,
  defaultWidth = 420,
  minWidth = 320,
  maxWidth = 860,
  label = 'Resize panel',
}: SidePanelDockProps) {
  const [width, setWidth] = React.useState(() => readStored(storageKey, minWidth, maxWidth, defaultWidth));
  const dragRef = React.useRef<{ startX: number; startWidth: number } | null>(null);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, String(width));
    } catch {
      // Not remembered; the width still holds for this visit.
    }
  }, [storageKey, width]);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      dragRef.current = { startX: e.clientX, startWidth: width };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      // The panel is on the right, so dragging left widens it.
      setWidth(clamp(drag.startWidth - (e.clientX - drag.startX), minWidth, maxWidth));
    },
    [minWidth, maxWidth],
  );

  const onPointerUp = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  if (!open) return <>{children}</>;

  return (
    // No gap on this row: the handle alone separates the two, so they sit as
    // close as the rest of the page's panels.
    <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1, minHeight: 0, height: '100%' }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
        }}
      >
        {children}
      </Box>

      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        title="Drag to resize · double-click to reset"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => setWidth(defaultWidth)}
        sx={(t) => ({
          flexShrink: 0,
          width: t.spacing(1.5),
          cursor: 'col-resize',
          position: 'relative',
          // A hairline down the middle that shows where to grab.
          '&::after': {
            content: '""',
            position: 'absolute',
            top: t.spacing(1),
            bottom: t.spacing(1),
            left: '50%',
            width: '1px',
            bgcolor: 'transparent',
            transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          },
          '&:hover::after, &:active::after': { bgcolor: t.tokens.brand.primary },
        })}
      />

      <Box sx={{ width, flexShrink: 0, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {panel}
      </Box>
    </Box>
  );
}
